import React, { Fragment, useCallback, useContext, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { UniversalCreateButton } from '@atlassian/universal-create';
import type { UniversalCreateMenuSection } from '@atlassian/universal-create/types';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { fg } from '@confluence/feature-gating';
import { START, END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useCanCreateSpace } from '@confluence/create-space/entry-points/createSpace.ts';
import { AppNavigationContext } from '@confluence/app-navigation-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { useSpaceEnabledContentTypes } from '../CreateContentPopover/useSpaceEnabledContentTypes.tsx';

import { useUniversalCreateState, useUniversalCreateActions } from './useUniversalCreate.ts';
import { ANALYTICS_EVENT_SOURCE, useMenuItems } from './useMenuItems/index.tsx';
import { useAISmartCreateItem } from './useMenuItems/useAISmartCreateItem.tsx';
import { useIsFreeOrStandardUser } from './useIsFreeOrStandardUser.ts';
import { useCwrSplitButtonExperiment } from './useCwrSplitButtonExperiment.ts';
import { UniversalCreateMenuSpotlight } from './useUniversalCreateMenuSpotlight.tsx';

export type AppDefinedMenuSection = Extract<
	UniversalCreateMenuSection,
	{ type: 'appDefinedSection' }
>;

const messages = defineMessages({
	create: {
		id: 'create-blank-fabric-page.universal-create.create.text',
		defaultMessage: 'Create',
		description: 'The Create text that appears in the top nav',
	},
});

export const getCreateButtonTheme = (createButtonTheme) => (current, props) => {
	const { buttonStyles, spinnerStyles } = current(props);
	return {
		buttonStyles: {
			...buttonStyles,
			...createButtonTheme.default,
			margin: 0,
			fontWeight: token('font.weight.medium'),
			':hover': createButtonTheme.hover,
			':focus': createButtonTheme.focus,
			// :active doesn't work in FF, because we do a
			// e.preventDefault() on mouse down in Button.
			// '&&' is required to add more CSS specificity
			// && it not a valid CSSObject property
			// @ts-ignore
			'&&': {
				...(props.state === 'active' && createButtonTheme.active),
			},
		},
		spinnerStyles,
	};
};

const Create = ({ flags }) => {
	const { isOpen } = useUniversalCreateState();
	const { setIsOpen, setOpenFrom } = useUniversalCreateActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { match } = useContext(RoutesContext);
	const { theme } = useContext(AppNavigationContext);
	const isOnEditRoute = useIsEditorPage();
	const { formatMessage } = useIntl();
	const { isRovoLLMEnabled, accessStatus, isLoggedIn, cloudId, orgId, userId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const { isLoading } = useSpaceEnabledContentTypes(spaceKey);
	const { loading: canCreateSpaceLoading } = useCanCreateSpace();
	const routeDataRef = useRouteDataRef();
	const isLivePage = useIsCurrentPageLive();
	const isFreeOrStandardUser = useIsFreeOrStandardUser();
	const { isEnabled: isCwrSplitButtonEnabled } = useCwrSplitButtonExperiment();
	const menuItems = useMenuItems(flags, () => setIsOpen(false), undefined, isOpen);
	const buttonTheme = theme?.mode?.create ? getCreateButtonTheme(theme.mode.create) : undefined;

	const hasAllItemsLoaded = !isLoading && !canCreateSpaceLoading;

	const onCreateClick = useCallback(() => {
		const willOpen = !isOpen;
		void setIsOpen(willOpen);

		// Only ask the choreographer to display the spotlight when this
		// click is opening the menu (not when it's toggling it shut).
		// `startMenuSpotlightRef.current` is null when the spotlight is not
		// mounted (user not in the CWR split-button cohort), so the optional
		// chaining makes this a safe no-op in that case.
		if (willOpen) {
			startMenuSpotlightRef.current?.();
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'globalCreateContentPopover',
				source: 'globalNavigation',
				attributes: {
					selectedItemPageContext: match?.name,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
					navdexPointType: START,
					isOnEditRoute,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isOpen, setIsOpen, match, isOnEditRoute]);

	// Duplicated from useMenuItems hook
	const handleAnalyticsAndResetStickySearch = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					source: ANALYTICS_EVENT_SOURCE,
					attributes: {
						navdexPointType: END,
						isOnEditRoute,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						...attributes,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute, routeDataRef, isLivePage],
	);

	const onSmartCreateClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			handleAnalyticsAndResetStickySearch(actionSubjectId, attributes);
			void setIsOpen(false);
		},
		[handleAnalyticsAndResetStickySearch, setIsOpen],
	);

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const isAIFCCreateEnabled = isRovoLLMEnabled && !isAnonymousUser && fg('aifc_create_enabled');

	const smartCreateItem = useAISmartCreateItem({
		source: 'globalCreateDropdown-smartCreate',
		spaceKey,
		onClick: onSmartCreateClick,
		isDisabled: !isAIFCCreateEnabled,
	});

	// Refs filled by `<UniversalCreateMenuSpotlight />` (when mounted) with
	// its imperative `startSpotlight` / `stopSpotlight` callbacks. Lets us
	// drive the choreographer entirely from event handlers (`onCreateClick`,
	// `onClose`) — never from a `useEffect` inside the spotlight hook.
	const startMenuSpotlightRef = useRef<(() => void) | null>(null);
	const stopMenuSpotlightRef = useRef<(() => void) | null>(null);
	const onStartMenuSpotlightChange = useCallback((startSpotlight: (() => void) | null) => {
		startMenuSpotlightRef.current = startSpotlight;
	}, []);
	const onStopMenuSpotlightChange = useCallback((stopSpotlight: (() => void) | null) => {
		stopMenuSpotlightRef.current = stopSpotlight;
	}, []);

	// Reset the openFrom state when the create menu is closed.
	const onClose = () => {
		void setIsOpen(false);
		void setOpenFrom('');
		stopMenuSpotlightRef.current?.();
	};

	const sections = useMemo(
		() =>
			menuItems.map((section) => ({
				type: 'appDefinedSection',
				...section,
			})) as AppDefinedMenuSection[],
		[menuItems],
	);

	const crossAppCreationConfig = useMemo(
		() =>
			hasAllItemsLoaded &&
			!isAnonymousUser &&
			!!cloudId &&
			isFreeOrStandardUser &&
			!isCwrSplitButtonEnabled &&
			expValEquals('navx_universal_create_confluence_menu_upgrade', 'cohort', 'variant_2')
				? {
						enabled: true,
						prefetch: true,
						cloudId: cloudId || '',
						orgId: orgId || '',
						accountId: userId || '',
					}
				: ({ enabled: false } as const),
		[
			hasAllItemsLoaded,
			isAnonymousUser,
			cloudId,
			isFreeOrStandardUser,
			isCwrSplitButtonEnabled,
			orgId,
			userId,
		],
	);

	return (
		<Fragment>
			<UniversalCreateButton
				sections={sections}
				crossAppCreationConfig={crossAppCreationConfig}
				onClick={onCreateClick}
				triggerButtonSpotlightTarget="top-nav-create"
				enableTriggerPulse={false}
				isOpen={isOpen}
				onClose={onClose}
				product="confluence"
				buttonTheme={buttonTheme}
				testId="create-content-popover-button"
				shouldRenderToParent
				{...(isAIFCCreateEnabled && isCwrSplitButtonEnabled
					? { primaryActionItem: smartCreateItem }
					: {})}
			>
				{formatMessage(messages.create)}
			</UniversalCreateButton>
			{isAIFCCreateEnabled && isCwrSplitButtonEnabled && (
				<UniversalCreateMenuSpotlight
					isMenuOpen={isOpen}
					onStartSpotlightChange={onStartMenuSpotlightChange}
					onStopSpotlightChange={onStopMenuSpotlightChange}
					onCtaDismissMenu={onClose}
				/>
			)}
		</Fragment>
	);
};

const CreateWithFlags = withFlags(Create);

export function UniversalCreate() {
	return (
		<TransparentErrorBoundary attribution={Attribution.NAVX}>
			<CreateWithFlags />
		</TransparentErrorBoundary>
	);
}
